import { upload } from '@vercel/blob/client'
import type { NoOrderPublicPage, NoOrderRegistrationInput, NoOrderRegistrationResult } from '../../shared/crm-no-order-needed.js'

import type {
  CrmEmbedBootstrapResponse,
  CrmEmbedClaimPayload,
  CrmEmbedClaimResult,
  CrmEmbedClientConfig,
  CrmEmbedPlatform,
  CrmEmbedProduct,
  CrmEmbedSample,
  CrmEmbedUploadedScreenshot,
} from './crm-embed-types'

const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024
const SCREENSHOT_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
])

const normalizeBaseUrl = (value: string) => value.trim().replace(/\/+$/, '')

const safePathSegment = (value: string, fallback: string) => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return normalized || fallback
}

const fileExtension = (file: File) => {
  const direct = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '')
  if (direct) return direct
  if (file.type === 'image/png') return 'png'
  if (file.type === 'image/webp') return 'webp'
  if (file.type === 'image/gif') return 'gif'
  if (file.type === 'image/heic') return 'heic'
  if (file.type === 'image/heif') return 'heif'
  return 'jpg'
}

const randomId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

export class CrmEmbedApiError extends Error {
  status: number
  code: string

  constructor(message: string, status = 500, code = 'CRM_EMBED_REQUEST_FAILED') {
    super(message)
    this.name = 'CrmEmbedApiError'
    this.status = status
    this.code = code
  }
}

export class CrmEmbedClient {
  readonly integrationId: string
  readonly apiBaseUrl: string
  readonly flowType: CrmEmbedClientConfig['flowType']
  readonly sourcePath: string
  readonly clientVersion: string

  private session: CrmEmbedBootstrapResponse | null = null
  private bootstrapPromise: Promise<CrmEmbedBootstrapResponse> | null = null

  constructor(config: CrmEmbedClientConfig) {
    this.integrationId = config.integrationId.trim()
    this.apiBaseUrl = normalizeBaseUrl(config.apiBaseUrl)
    this.flowType = config.flowType
    this.sourcePath = config.sourcePath
    this.clientVersion = config.clientVersion || '1.1.0'

    if (!this.integrationId || !this.apiBaseUrl || !this.sourcePath) {
      throw new CrmEmbedApiError(
        'CRM integration ID, API URL and source path are required.',
        400,
        'CRM_EMBED_CLIENT_CONFIG_INVALID',
      )
    }
  }

  private endpoint(pathname: string, params: Record<string, string> = {}) {
    const url = new URL(`${this.apiBaseUrl}/${pathname.replace(/^\/+/, '')}`)
    url.searchParams.set('integrationId', this.integrationId)
    for (const [key, value] of Object.entries(params)) {
      if (value) url.searchParams.set(key, value)
    }
    return url.toString()
  }

  private scopeHeaders(token = '') {
    return {
      'X-CRM-Embed-Path': this.sourcePath,
      'X-CRM-Flow-Type': this.flowType,
      'X-CRM-Embed-Client-Version': this.clientVersion,
      'X-CRM-Integration-Id': this.integrationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    const text = await response.text()
    let payload: Record<string, unknown> = {}
    if (text) {
      try {
        payload = JSON.parse(text) as Record<string, unknown>
      } catch {
        payload = {}
      }
    }

    if (!response.ok) {
      throw new CrmEmbedApiError(
        String(payload.message || `CRM request failed with HTTP ${response.status}.`),
        response.status,
        String(payload.code || 'CRM_EMBED_REQUEST_FAILED'),
      )
    }

    return payload as T
  }

  async bootstrap(force = false) {
    if (!force && this.session && Date.parse(this.session.expiresAt) > Date.now() + 30_000) {
      return this.session
    }
    if (!force && this.bootstrapPromise) return this.bootstrapPromise

    this.bootstrapPromise = fetch(this.endpoint('bootstrap'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.scopeHeaders(),
      },
      body: JSON.stringify({
        integrationId: this.integrationId,
        sourcePath: this.sourcePath,
        flowType: this.flowType,
        clientVersion: this.clientVersion,
      }),
    })
      .then((response) => this.parseResponse<CrmEmbedBootstrapResponse>(response))
      .then((session) => {
        this.session = session
        return session
      })
      .finally(() => {
        this.bootstrapPromise = null
      })

    return this.bootstrapPromise
  }

  private async authorizedRequest<T>(
    pathname: string,
    init: RequestInit,
    params: Record<string, string> = {},
    canRetry = true,
  ): Promise<T> {
    const session = await this.bootstrap()
    const response = await fetch(this.endpoint(pathname, params), {
      ...init,
      headers: {
        ...this.scopeHeaders(session.sessionToken),
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
    })

    if (response.status === 401 && canRetry) {
      await this.bootstrap(true)
      return this.authorizedRequest<T>(pathname, init, params, false)
    }

    return this.parseResponse<T>(response)
  }

  getNoOrderPage(receipt = '') {
    return this.authorizedRequest<NoOrderPublicPage>('no-order-needed', { method: 'GET' }, { receipt })
  }

  registerNoOrder(input: NoOrderRegistrationInput) {
    return this.authorizedRequest<NoOrderRegistrationResult>('no-order-needed', {
      method: 'POST', body: JSON.stringify(input),
    })
  }

  async getProducts(platformKey: CrmEmbedPlatform, orderId: string) {
    const payload = await this.authorizedRequest<{ products?: CrmEmbedProduct[] }>(
      'products',
      { method: 'GET' },
      { platformKey, orderId },
    )
    return Array.isArray(payload.products) ? payload.products : []
  }

  verifyProductCode(platformKey: CrmEmbedPlatform, code: string) {
    return this.authorizedRequest<{
      valid: boolean
      used?: boolean
      product?: CrmEmbedProduct | null
      products?: CrmEmbedProduct[]
      requiresProductSelection?: boolean
      message?: string
    }>('product-code', { method: 'GET' }, { platformKey, code })
  }

  async getSamples(platformKey: CrmEmbedPlatform, externalProductId: string) {
    const payload = await this.authorizedRequest<{ samples?: CrmEmbedSample[] }>(
      'samples',
      { method: 'GET' },
      { platformKey, externalProductId },
    )
    return Array.isArray(payload.samples) ? payload.samples : []
  }

  saveClaim(payload: CrmEmbedClaimPayload) {
    return this.authorizedRequest<CrmEmbedClaimResult>('claims', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        sourcePath: this.sourcePath,
        flowType: this.flowType,
        clientVersion: this.clientVersion,
      }),
    })
  }

  trackFlowEvent(eventType: 'form_view' | 'step_one_submitted', visitorId: string) {
    return this.authorizedRequest<{ recorded?: boolean }>('analytics', {
      method: 'POST',
      body: JSON.stringify({ eventType, visitorId }),
    })
  }

  async uploadScreenshot(file: File, siteId: string): Promise<CrmEmbedUploadedScreenshot> {
    if (!SCREENSHOT_TYPES.has(file.type)) {
      throw new CrmEmbedApiError(
        'Please upload a JPG, PNG, WEBP, GIF, HEIC or HEIF image.',
        400,
        'CRM_EMBED_SCREENSHOT_TYPE_INVALID',
      )
    }
    if (file.size > SCREENSHOT_MAX_BYTES) {
      throw new CrmEmbedApiError(
        'The screenshot must be 5MB or smaller.',
        400,
        'CRM_EMBED_SCREENSHOT_TOO_LARGE',
      )
    }

    const session = await this.bootstrap()
    const uploadUrl = new URL('/api/blob/upload', this.apiBaseUrl)
    uploadUrl.searchParams.set('integrationId', this.integrationId)
    const pathname =
      `crm/claims/${safePathSegment(siteId, 'site')}/screenshots/` +
      `${Date.now()}-${randomId()}.${fileExtension(file)}`
    const result = await upload(pathname, file, {
      access: 'public',
      handleUploadUrl: uploadUrl.toString(),
      contentType: file.type || undefined,
      clientPayload: JSON.stringify({
        scope: 'crm-registration-screenshot',
        siteId,
        integrationId: this.integrationId,
        sessionToken: session.sessionToken,
        sourcePath: this.sourcePath,
        flowType: this.flowType,
        clientVersion: this.clientVersion,
      }),
      headers: this.scopeHeaders(session.sessionToken),
    })

    return {
      url: result.url,
      name: file.name,
      mimeType: file.type,
      bytes: file.size,
    }
  }
}
